<template>
    <div class="tw:relative tw:inline-flex">
        <!-- Trigger slot -->
        <div
            ref="triggerRef"
            @mouseenter="onMouseEnter"
            @mouseleave="onMouseLeave"
            @click="onClickTrigger"
            class="tw:cursor-pointer"
        >
            <slot name="trigger">
                <i class="material-icons tw:text-sm tw:text-slate-400 tw:hover:text-slate-600">info</i>
            </slot>
        </div>

        <!-- Popover panel -->
        <transition name="popover">
            <div
                v-if="isOpen"
                ref="popoverRef"
                class="tw:absolute tw:z-50 tw:bg-white tw:border tw:border-slate-200 tw:rounded-xl tw:shadow-lg tw:p-3 tw:min-w-[180px] tw:max-w-[260px] tw:text-xs tw:text-slate-700"
                :class="positionClasses"
                @mouseenter="onPopoverEnter"
                @mouseleave="onMouseLeave"
            >
                <!-- Close button on mobile -->
                <button
                    class="tw:absolute tw:top-2 tw:right-2 tw:text-slate-400 tw:hover:text-slate-600 tw:md:hidden"
                    @click.stop="isOpen = false"
                >
                    <i class="material-icons tw:text-sm">close</i>
                </button>
                <slot></slot>
            </div>
        </transition>
    </div>
</template>

<script setup>
    import { ref, onMounted, onUnmounted } from 'vue';

    const props = defineProps({
        position: { type: String, default: 'top' }, // top | bottom | left | right
    });

    const isOpen = ref(false);
    const triggerRef = ref(null);
    const popoverRef = ref(null);
    let closeTimer = null;

    const positionClasses = (() => {
        const map = {
            top:    'tw:bottom-full tw:left-1/2 tw:-translate-x-1/2 tw:mb-2',
            bottom: 'tw:top-full tw:left-1/2 tw:-translate-x-1/2 tw:mt-2',
            left:   'tw:right-full tw:top-1/2 tw:-translate-y-1/2 tw:mr-2',
            right:  'tw:left-full tw:top-1/2 tw:-translate-y-1/2 tw:ml-2',
        };
        return map[props.position] ?? map.top;
    })();

    const isMobile = () => window.innerWidth < 768;

    const onMouseEnter = () => {
        if (isMobile()) return;
        clearTimeout(closeTimer);
        isOpen.value = true;
    };

    const onMouseLeave = () => {
        if (isMobile()) return;
        closeTimer = setTimeout(() => { isOpen.value = false; }, 150);
    };

    const onPopoverEnter = () => {
        if (isMobile()) return;
        clearTimeout(closeTimer);
    };

    const onClickTrigger = () => {
        if (!isMobile()) return;
        isOpen.value = !isOpen.value;
    };

    // Close on outside click (mobile)
    const onClickOutside = e => {
        if (!triggerRef.value?.contains(e.target) && !popoverRef.value?.contains(e.target)) {
            isOpen.value = false;
        }
    };

    onMounted(() => document.addEventListener('click', onClickOutside));
    onUnmounted(() => {
        document.removeEventListener('click', onClickOutside);
        clearTimeout(closeTimer);
    });
</script>

<style scoped>
.popover-enter-active,
.popover-leave-active {
    transition: opacity 0.15s ease, transform 0.15s ease;
}
.popover-enter,
.popover-leave-to {
    opacity: 0;
    transform: scale(0.95);
}
</style>
